import React, { useEffect, useState } from 'react';
import styled from 'styled-components';
import { Link, useNavigate } from 'react-router-dom';
import { Accounts as MeteorAccounts } from 'meteor/accounts-base';
import { toast } from 'react-toastify';
import { strings } from '/imports/strings';
import { css } from '/imports/ui/css';
import { queryStringToObject } from '/imports/utils';
import { Button } from '/imports/ui/Button';
import { Input } from '/imports/ui/Input';
import { PhoneInput } from '/imports/ui/PhoneInput';
import { PublicFooter } from '/imports/ui/PublicFooter';
import { SignedOutPageStyled } from '/imports/ui/SignedOutPage';
import { LoadingFullScreen } from '/imports/ui/LoadingFullScreen';
import { accountRoleLabel, acceptAccountInvitation, getAccountInvitation } from '/imports/api/accounts';

const AcceptInvitationPageStyled = styled(SignedOutPageStyled)`
  /* align-self: the wrapper is a flex column that stretches children to full width, which distorts an img. */
  .brand { height: 8rem; width: auto; align-self: center; margin-bottom: 1rem; }
  .intro { margin-bottom: 1.5rem; line-height: 1.5; }
  .intro .lead { font-size: 1.05rem; margin: 0.25rem 0 0; }
  .intro .as { color: ${css.grey4}; font-size: 0.9rem; margin: 0.25rem 0 0; }
  .intro .prompt { margin: 1rem 0 0; }
  .roles { display: flex; flex-wrap: wrap; justify-content: center; align-items: center; gap: 0.4rem; margin-top: 0.75rem; }
  .roles .lbl { color: ${css.grey4}; font-size: 0.8rem; }
  .roles .pill { background: ${css.grey8}; color: ${css.grey2}; border-radius: 1rem; padding: 0.15rem 0.65rem; font-size: 0.8rem; }
  /* Breathing room between the fields, and less of a void before the button. */
  .wrapper .inner-wrapper form .inputs { gap: 0.75rem; margin-bottom: 1rem; }
`;

/**
 * Public landing page for an emailed invitation (/accept-invitation?jwt=…). Shows what the token grants,
 * takes the recipient's name (+ optional phone), redeems it, and signs them straight in to the org.
 */
export const AcceptInvitationPage = () => {
  const navigate = useNavigate();
  const [jwt] = useState<string>(() => queryStringToObject().jwt || '');
  const [invitation, setInvitation] = useState<{ orgName: string, email: string, roles: string[] }>();
  const [invalid, setInvalid] = useState(false);
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [phone, setPhone] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (!jwt) { setInvalid(true); return; }
    (async () => {
      try { setInvitation(await getAccountInvitation.call(jwt)); }
      catch (_) { setInvalid(true); }
    })();
  }, [jwt]);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setSubmitting(true);
    try {
      const { loginJwt } = await acceptAccountInvitation.call({ jwt, firstName: firstName.trim(), lastName: lastName.trim(), phone: phone.trim() || undefined });
      // Trade the one-shot login token for a session — the same loginWithJwt handler the emailed sign-in link uses.
      MeteorAccounts.callLoginMethod({
        methodArguments: [{ jwt: loginJwt }],
        userCallback(error) {
          if (error) { toast.error(strings.InvalidOrExpiredVerificationToken()); setSubmitting(false); }
          else navigate('/');
        },
      });
    }
    catch (error: any) {
      toast.error(error?.reason || error?.message || strings.AnErrorOccurredPleaseTryAgainOrContactSupport());
      setSubmitting(false);
    }
  };

  if (!invitation && !invalid) return <LoadingFullScreen />;
  return (
    <AcceptInvitationPageStyled className="page">
      <div className="wrapper">
        <div className="inner-wrapper">
          <img className="brand" src="/images/logo_dark.png" alt="Luminia" />
          {
            invalid || !invitation
              ? <>
                <div><h2>{strings.AcceptInvitation()}</h2><div>{strings.InvalidOrExpiredInvitation()}</div></div>
                <Link className="forgot-password" to="/login">{strings.BackToLogin()}</Link>
              </>
              : <>
                <div className="intro">
                  <h2>{strings.AcceptInvitation()}</h2>
                  <p className="lead">{strings.YouHaveBeenInvitedToJoin()} <b>{invitation.orgName}</b></p>
                  <p className="as">{strings.SigningUpAs(invitation.email)}</p>
                  {!!invitation.roles.length && (
                    <div className="roles">
                      <span className="lbl">{strings.Roles()}:</span>
                      {invitation.roles.map((r) => <span key={r} className="pill">{accountRoleLabel(r)}</span>)}
                    </div>
                  )}
                  <p className="prompt">{strings.TellUsYourNameToFinish()}</p>
                </div>
                <form onSubmit={onSubmit}>
                  <div className="inputs">
                    <Input placeholder={strings.FirstName()} value={firstName} inputId="first-name-input" onChange={(v) => setFirstName(v || '')} autoFocus />
                    <Input placeholder={strings.LastName()} value={lastName} inputId="last-name-input" onChange={(v) => setLastName(v || '')} />
                    <PhoneInput placeholder={strings.PhoneNumberOptional()} value={phone} inputId="phone-input" onChange={(v) => setPhone(v || '')} />
                  </div>
                  <Button id="accept-submit" type="submit" disabled={submitting || !firstName.trim() || !lastName.trim()}><div>{strings.JoinOrganization()}</div></Button>
                </form>
              </>
          }
        </div>
      </div>
      <PublicFooter />
    </AcceptInvitationPageStyled>
  );
};
